import React, { useState, useEffect, useRef } from 'react'
import {
  dashboardService,
  AvailabilityData,
  WorkloadData,
  ComplianceData,
  HealthData,
} from './dashboardService'
import { useAuth } from '../auth/AuthContext'
import { AvailabilitySegmentedBar } from './AvailabilitySegmentedBar'
import { WorkloadStackedChart } from './WorkloadStackedChart'
import { WellnessSmallCharts } from './WellnessSmallCharts'
import { BodyDiscomfortPanel } from './BodyDiscomfortPanel'
import { PendingActionsList } from './PendingActionsList'
import {
  Users,
  ShieldCheck,
  Flame,
  Moon,
  HeartPulse,
  FileCheck2,
  ExternalLink,
  Lock,
} from 'lucide-react'

interface TeamTrackingViewProps {
  onSelectAthlete?: (athlete: any) => void
  onNavigateToHealth?: () => void
  onNavigateToStats?: () => void
  isCoachView?: boolean
  teamId?: string
  camp?: string
  inactive?: boolean
  from?: string
  to?: string
  athletes?: Array<{
    id: string
    name?: string
    first_name?: string
    last_name?: string
    primary_position?: string
    position?: string
    dorsal_number?: number
    dorsal?: number
    tracking_status?: string
    unit?: string
  }>
}

export const TeamTrackingView: React.FC<TeamTrackingViewProps> = ({
  onSelectAthlete,
  onNavigateToHealth,
  onNavigateToStats,
  isCoachView = false,
  teamId: propTeamId,
  camp,
  inactive = false,
  from,
  to,
  athletes: propAthletes,
}) => {
  let user = null
  try {
    const auth = useAuth()
    user = auth?.user ?? null
  } catch {
    user = null
  }
  const teamId = propTeamId || user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'

  const [availability, setAvailability] = useState<AvailabilityData | null>(null)
  const [workload, setWorkload] = useState<WorkloadData | null>(null)
  const [compliance, setCompliance] = useState<ComplianceData | null>(null)
  const [health, setHealth] = useState<HealthData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  const activeRequestId = useRef(0)

  useEffect(() => {
    let isMounted = true
    const reqId = ++activeRequestId.current

    async function loadRealData() {
      setLoading(true)
      try {
        const [availRes, workRes, compRes] = await Promise.all([
          dashboardService.getData<AvailabilityData>('availability', { teamId, camp, inactive, from, to }).catch(() => null),
          dashboardService.getData<WorkloadData>('workload', { teamId, camp, inactive, from, to }).catch(() => null),
          dashboardService.getData<ComplianceData>('compliance', { teamId, camp, inactive, from, to }).catch(() => null),
        ])

        if (!isMounted || activeRequestId.current !== reqId) return
        setAvailability(availRes)
        setWorkload(workRes)
        setCompliance(compRes)

        // Solo solicitar salud privada si NO es coach (respeto estricto a PERMISSIONS_MATRIX)
        if (!isCoachView) {
          const healthRes = await dashboardService
            .getData<HealthData>('health', { teamId, camp, inactive, from, to })
            .catch(() => null)
          if (isMounted && activeRequestId.current === reqId) setHealth(healthRes)
        }
      } catch (err) {
        console.warn('Error cargando datos de seguimiento del equipo:', err)
      } finally {
        if (isMounted && activeRequestId.current === reqId) setLoading(false)
      }
    }

    loadRealData()
    return () => {
      isMounted = false
    }
  }, [teamId, isCoachView, camp, inactive, from, to])

  // Población canónica en el ámbito seleccionado
  const athletesInScope = (propAthletes && propAthletes.length > 0)
    ? propAthletes.map((a: any) => ({
        id: a.id,
        name: a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Atleta',
        dorsal: a.dorsal ?? a.dorsal_number ?? 0,
        position: a.position || a.primary_position || 'ATH',
        unit: (a.unit || 'Ataque') as 'Ataque' | 'Defensa',
      }))
    : (availability?.rows || []).map((r) => ({
        id: r.athlete_id,
        name: r.first_name ? `${r.first_name} ${r.last_name || ''}`.trim() : 'Atleta',
        dorsal: 0,
        position: 'ATH',
        unit: 'Ataque' as 'Ataque' | 'Defensa',
      }))

  // Reconciliación 1-a-1 de disponibilidad: cada atleta del ámbito tiene exactamente una categoría
  const rowMap = new Map((availability?.rows || []).map((r) => [r.athlete_id, r]))

  const reconciledAthletes = athletesInScope.map((a) => {
    const row = rowMap.get(a.id)
    const rawStatus = row?.status || 'not_assessed'
    const status: 'available' | 'limited' | 'needs_assessment' | 'unavailable' | 'not_assessed' =
      rawStatus === 'available' ? 'available' :
      rawStatus === 'limited' ? 'limited' :
      rawStatus === 'needs_assessment' ? 'needs_assessment' :
      rawStatus === 'unavailable' ? 'unavailable' :
      'not_assessed'

    const restriction = row?.restriction || row?.notes
    const reviewPending = !!row?.review_pending

    return {
      id: a.id,
      name: a.name,
      dorsal: a.dorsal,
      position: a.position,
      unit: a.unit,
      club: 'FEFA',
      status,
      restriction,
      reviewPending,
      availability: (
        status === 'available' ? 'available_100' :
        status === 'limited' ? 'limited' :
        status === 'needs_assessment' ? 'needs_assessment' :
        status === 'unavailable' ? 'unavailable' :
        'not_assessed'
      ) as 'available_100' | 'limited' | 'needs_assessment' | 'unavailable' | 'not_assessed',
      availabilityStatus: (
        status === 'available' ? 'apto' :
        status === 'limited' ? 'restringido' :
        'sin_valoracion'
      ) as 'apto' | 'restringido' | 'sin_valoracion',
      availabilityReason: restriction || (reviewPending ? 'Revisión periódica pendiente' : (status === 'not_assessed' ? 'Sin valoración médica vigente' : undefined)),
      isCalledUp: true,
    }
  })

  // Conteos exactos por categoría reconciliada
  const availableCount = reconciledAthletes.filter((a) => a.status === 'available').length
  const limitedCount = reconciledAthletes.filter((a) => a.status === 'limited').length
  const needsAssessmentCount = reconciledAthletes.filter((a) => a.status === 'needs_assessment').length
  const unavailableCount = reconciledAthletes.filter((a) => a.status === 'unavailable').length
  const notAssessedCount = reconciledAthletes.filter((a) => a.status === 'not_assessed').length
  const totalTracked = reconciledAthletes.length

  // Identidad matemática garantizada: availableCount + limitedCount + needsAssessmentCount + unavailableCount + notAssessedCount === totalTracked
  const availabilityPct = totalTracked > 0 ? Math.round((availableCount / totalTracked) * 100) : null

  // Carga semanal
  const latestWeekLoad = workload?.weeks?.[0]
  const avgLoadMin = latestWeekLoad?.load !== undefined ? Math.round(latestWeekLoad.load) : null
  const unconfirmedSessions = workload?.unconfirmed ?? 0

  // Cumplimiento
  const latestCompliance = compliance?.weeks?.[0]
  const submittedForms = latestCompliance?.submitted ?? 0
  const expectedForms = latestCompliance?.expected ?? 0
  const complianceRate = latestCompliance?.rate !== null && latestCompliance?.rate !== undefined
    ? Math.round(latestCompliance.rate)
    : expectedForms > 0
    ? Math.round((submittedForms / expectedForms) * 100)
    : null

  // Salud / Sueño / Recuperación independiente
  const validHealthResponses = health?.responses || []
  const sleepSum = validHealthResponses.reduce((acc, r) => acc + (Number(r.payload?.sleep_hours) || 0), 0)
  const sleepCount = validHealthResponses.filter((r) => r.payload?.sleep_hours !== undefined).length
  const avgSleepHours = sleepCount > 0 ? Number((sleepSum / sleepCount).toFixed(1)) : null

  // Recuperación independiente (WeeklyFormPayload.recovery 1..5) - NUNCA invertida de fatiga
  const recSum = validHealthResponses.reduce((acc, r) => acc + (Number(r.payload?.recovery) || 0), 0)
  const recCount = validHealthResponses.filter((r) => r.payload?.recovery !== undefined).length
  const avgRecovery = recCount > 0 ? Number((recSum / recCount).toFixed(1)) : null

  // Fatiga (WeeklyFormPayload.fatigue 1..5)
  const fatSum = validHealthResponses.reduce((acc, r) => acc + (Number(r.payload?.fatigue) || 0), 0)
  const fatCount = validHealthResponses.filter((r) => r.payload?.fatigue !== undefined).length
  const avgFatigue = fatCount > 0 ? Number((fatSum / fatCount).toFixed(1)) : null

  // Atletas reconciliados para AvailabilitySegmentedBar
  const mappedAthletes = reconciledAthletes

  if (loading && !availability) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted, #64748b)' }}>
        Cargando datos de seguimiento del equipo...
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* KPI Row (5 metrics + Compact Compliance Card) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '0.85rem',
        }}
      >
        {/* KPI 1: Jugadores en seguimiento activo */}
        <div
          style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '0.9rem 1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Seguimiento Activo
            </span>
            <Users size={15} color="var(--nav-bg-base, #0a1320)" />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
            {totalTracked}{' '}
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>atletas</span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
            {totalTracked > 0 ? `Censo en seguimiento · n=${totalTracked}` : 'Sin deportistas registrados'}
          </div>
        </div>

        {/* KPI 2: Disponibilidad sanitaria publicada */}
        <div
          style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '0.9rem 1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Disponibilidad
            </span>
            <ShieldCheck size={15} color="#15803d" />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
            {availabilityPct !== null ? `${availabilityPct}%` : '0%'}{' '}
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>
              ({availableCount}/{totalTracked} aptos)
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px', lineHeight: 1.3 }}>
            {limitedCount} restringidos · {needsAssessmentCount} pendientes · {notAssessedCount} sin valoración (suma: {availableCount + limitedCount + needsAssessmentCount + unavailableCount + notAssessedCount}/{totalTracked})
          </div>
        </div>

        {/* KPI 3: Carga semanal media sRPE */}
        <div
          style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '0.9rem 1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Carga Semanal Media
            </span>
            <Flame size={15} color="#d97706" />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
            {avgLoadMin !== null ? avgLoadMin : 'N/D'}{' '}
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>
              UA (sRPE)
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
            {latestWeekLoad ? `${latestWeekLoad.sessions} sesiones · ${latestWeekLoad.days} días activos` : 'Sin registros de entrenamiento'}
          </div>
        </div>

        {/* KPI 4: Horas medias de sueño (o Volumen en Campo si es vista coach) */}
        <div
          style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '0.9rem 1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              {isCoachView ? 'Volumen Deportivo' : 'Sueño Medio'}
            </span>
            <Moon size={15} color="#4338ca" />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
            {isCoachView
              ? (latestWeekLoad?.load ? `${latestWeekLoad.load} UA` : 'N/D')
              : (avgSleepHours !== null ? `${avgSleepHours} h` : 'N/D')}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
            {isCoachView
              ? (unconfirmedSessions > 0 ? `${unconfirmedSessions} sesiones sin confirmar` : 'Carga confirmada')
              : (sleepCount > 0 ? `Promedio autodeclarado · n=${sleepCount}` : 'Datos no disponibles')}
          </div>
        </div>

        {/* KPI 5: Fatiga y Recuperación como métricas separadas e independientes */}
        <div
          style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '0.9rem 1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              {isCoachView ? 'Aptitud Colectiva' : 'Fatiga · Recuperación'}
            </span>
            <HeartPulse size={15} color="var(--brand-red, #b91c1c)" />
          </div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
            {isCoachView ? (
              availabilityPct !== null && availabilityPct >= 80 ? 'Alta' : 'Seguimiento'
            ) : avgFatigue !== null && avgRecovery !== null ? (
              `${avgFatigue} · ${avgRecovery}`
            ) : (
              'N/D'
            )}
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary, #475569)', marginLeft: '4px' }}>
              {isCoachView ? `(${availableCount}/${totalTracked})` : '(escala 1..5)'}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
            {isCoachView
              ? 'Disponibilidad operativa'
              : recCount > 0
              ? `Fatiga media ${avgFatigue} vs Recup. independiente ${avgRecovery} · n=${recCount}`
              : 'Información médica restringida'}
          </div>
        </div>

        {/* KPI 6: Cumplimiento de Formularios */}
        <div
          style={{
            background: 'var(--surface-subtle, #f8fafc)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '0.9rem 1rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
                Cumplimiento Semanal
              </span>
              <FileCheck2 size={15} color="#0284c7" />
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
              {submittedForms}/{expectedForms}{' '}
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#0284c7' }}>
                {complianceRate !== null ? `(${complianceRate}%)` : '(N/D)'}
              </span>
            </div>
          </div>

          <div style={{ marginTop: '6px' }}>
            {onNavigateToHealth ? (
              <button
                type="button"
                onClick={onNavigateToHealth}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: 'var(--brand-red, #b91c1c)',
                  cursor: 'pointer',
                }}
              >
                <span>Ver módulo Formularios</span>
                <ExternalLink size={11} />
              </button>
            ) : (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted, #64748b)' }}>
                {latestCompliance?.drafts || 0} borradores · {latestCompliance?.pending || 0} pendientes
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Barra de Disponibilidad Operativa */}
      <AvailabilitySegmentedBar
        athletes={mappedAthletes as any}
        onSelectAthlete={onSelectAthlete}
        compact={false}
      />

      {/* Gráfico de Carga de Entrenamiento */}
      <WorkloadStackedChart
        onNavigateToStats={onNavigateToStats}
        compact={false}
      />

      {/* Gráficos de Sueño y Bienestar (Solo visible para personal autorizado, nunca para coach sin rol médico) */}
      {!isCoachView ? (
        <WellnessSmallCharts />
      ) : (
        <div
          style={{
            background: 'var(--surface-subtle, #f8fafc)',
            border: '1px dashed var(--surface-border, #cbd5e1)',
            borderRadius: '8px',
            padding: '1rem',
            textAlign: 'center',
            color: 'var(--text-secondary, #475569)',
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Lock size={15} color="var(--text-muted, #64748b)" />
          <span>
            Detalle clínico, sueño y bienestar privado reservados a Servicios Médicos según PERMISSIONS_MATRIX.
          </span>
        </div>
      )}

      {/* Panel inferior: Molestias por zona corporal y Acciones pendientes */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '1.25rem',
          alignItems: 'start',
        }}
      >
        <BodyDiscomfortPanel
          onSelectAthlete={(athId) => {
            const found = mappedAthletes.find((a) => a.id === athId)
            if (found && onSelectAthlete) onSelectAthlete(found)
          }}
          isCoachView={isCoachView}
        />

        <PendingActionsList
          onSelectAction={(act) => {
            const found = mappedAthletes.find((a) => a.id === act.athleteId)
            if (found && onSelectAthlete) onSelectAthlete(found)
          }}
          compact={false}
        />
      </div>
    </div>
  )
}
export default TeamTrackingView
